import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";

const teamModels = [
  {
    name: "Micro Product Pod",
    bestFor: "Startups, SaaS founders and agencies starting with a compact team.",
    team: "3-5 people",
    points: ["Backend developer", "Frontend developer", "QA support", "Part-time PM"],
  },
  {
    name: "Dedicated Offshore Team",
    bestFor: "Companies that need long-term engineering capacity.",
    team: "5-15 people",
    points: ["Full-time dedicated team", "Sprint planning", "Weekly demos", "Delivery reporting"],
  },
  {
    name: "BOT Lite",
    bestFor: "Clients who may later want to transfer or own the capability.",
    team: "Custom",
    points: ["Build under Variable Soft", "Operate with governance", "Future transfer option", "Low-risk start"],
  },
];

const capabilities = [
  ".NET / C# / ASP.NET MVC",
  "React dashboards and portals",
  "PHP, Python and Java",
  "CRM and ERP development",
  "Dialer, IVR and call workflows",
  "AI bots and automation",
  "WhatsApp and third-party APIs",
  "QA, support and maintenance",
];

const faqs = [
  {
    question: "What is a Micro-GCC?",
    answer:
      "A Micro-GCC is a smaller Global Capability Center model where you start with a compact dedicated software team in India instead of setting up a full office, company, HR and payroll operation from day one.",
  },
  {
    question: "How is this different from normal outsourcing?",
    answer:
      "Traditional outsourcing is usually project based. A Micro-GCC gives you a dedicated long-term team that works like an extension of your business while Variable Soft manages hiring, onboarding, infrastructure and delivery governance.",
  },
  {
    question: "What team size can we start with?",
    answer:
      "Most clients can start with 3 to 5 people, such as a backend developer, frontend developer, QA engineer and part-time project manager. The team can scale later based on delivery needs.",
  },
  {
    question: "Which countries can use this model?",
    answer:
      "This is useful for companies in India, the United States, United Kingdom, Australia, Canada, Europe and other markets that need reliable offshore software development capacity.",
  },
];

export default function MicroGccIndiaPage() {
  const [teamSize, setTeamSize] = useState(5);
  const [localCost, setLocalCost] = useState(9000);
  const [indiaCost, setIndiaCost] = useState(2800);
  const [buffer, setBuffer] = useState(15);

  useEffect(() => {
    document.title = "Micro-GCC & Dedicated Offshore Development Teams in India | Variable Soft";

    const description =
      "Build a dedicated offshore software development team or Micro-GCC in India with Variable Soft. Start with 3-5 engineers for CRM, automation, web apps, QA and support.";
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, []);

  const estimate = useMemo(() => {
    const localMonthly = teamSize * localCost;
    const indiaMonthly = teamSize * indiaCost * (1 + buffer / 100);
    const monthlySaving = Math.max(0, localMonthly - indiaMonthly);
    const yearlySaving = monthlySaving * 12;
    const savingPercent = localMonthly ? Math.round((monthlySaving / localMonthly) * 100) : 0;

    return {
      localMonthly,
      indiaMonthly,
      monthlySaving,
      yearlySaving,
      savingPercent,
    };
  }, [teamSize, localCost, indiaCost, buffer]);

  const money = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  return (
    <div className="min-h-screen bg-white text-slate-800" style={{ fontFamily: "Roboto, sans-serif" }}>
      <Navigation />

      <section className="relative overflow-hidden bg-slate-950 pt-28 text-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(1000px_circle_at_15%_10%,rgba(59,130,246,0.24),transparent_55%),radial-gradient(820px_circle_at_90%_70%,rgba(20,184,166,0.18),transparent_55%)]" />
          <div className="absolute inset-0 opacity-[0.14] [background-image:linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:52px_52px]" />
          <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_50%_35%,transparent_30%,rgba(0,0,0,0.72)_100%)]" />
        </div>

        <div className="relative z-10 mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            className="lg:col-span-7"
          >
            <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold tracking-widest text-sky-200">
              MICRO-GCC AND DEDICATED OFFSHORE TEAMS
            </span>
            <h1 className="mt-7 text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
              Build your India software team without setting up a company.
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/72">
              Variable Soft helps startups, agencies and growing businesses create dedicated
              offshore software development teams and Micro-GCCs in India, starting with
              3 to 5 engineers and scaling into a full product team.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href="/getintouch"
                className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-sky-400 to-blue-600 px-7 py-4 font-bold text-white shadow-lg shadow-blue-950/30"
              >
                Plan Your Offshore Team
              </a>
              <a
                href="#savings-calculator"
                className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-7 py-4 font-semibold text-white hover:bg-white/10"
              >
                Estimate Savings
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 text-sm text-white/72">
              {["Dedicated developers", "CRM and automation", "BOT Lite option", "Sprint delivery"].map((item) => (
                <span key={item} className="rounded-full border border-white/10 bg-white/5 px-4 py-2">
                  {item}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="rounded-[32px] border border-white/10 bg-white/[0.08] p-6 shadow-[0_40px_110px_-60px_rgba(0,0,0,0.9)] backdrop-blur-xl">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/50">Starter pod</p>
                  <h2 className="mt-1 text-2xl font-extrabold">3-5 person team</h2>
                </div>
                <span className="rounded-full bg-emerald-400/10 px-3 py-1.5 text-sm font-semibold text-emerald-200">
                  Low-risk start
                </span>
              </div>
              <div className="mt-6 space-y-3">
                {[
                  "Backend / API developer",
                  "Frontend dashboard developer",
                  "QA and release support",
                  "Automation or CRM specialist",
                  "Part-time project manager",
                ].map((role, index) => (
                  <div key={role} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-black/18 px-4 py-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-sm font-black text-slate-950">
                      {index + 1}
                    </span>
                    <span className="font-semibold text-white/84">{role}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {[
                  ["2-4x", "cost efficient"],
                  ["BOT Lite", "optional"],
                  ["Agile", "weekly demos"],
                ].map(([value, label]) => (
                  <div key={label} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
                    <div className="text-xl font-black text-sky-200">{value}</div>
                    <div className="mt-1 text-xs uppercase tracking-wider text-white/46">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-18 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              More control than outsourcing
            </span>
            <h2 className="mt-4 text-3xl font-black text-slate-950 sm:text-4xl">
              Less complexity than a full enterprise GCC.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Get a dedicated team that works like your extended product department while
              Variable Soft handles local operations, onboarding, delivery governance and support.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {teamModels.map((model) => (
              <div key={model.name} className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.5)]">
                <div className="text-sm font-bold uppercase tracking-widest text-primary">{model.team}</div>
                <h3 className="mt-3 text-2xl font-black text-slate-950">{model.name}</h3>
                <p className="mt-3 text-slate-600">{model.bestFor}</p>
                <ul className="mt-6 space-y-3">
                  {model.points.map((point) => (
                    <li key={point} className="flex gap-3 text-sm font-semibold text-slate-700">
                      <span className="mt-1 h-2.5 w-2.5 rounded-full bg-primary" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="savings-calculator" className="bg-slate-50 py-18 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              Team cost calculator
            </span>
            <h2 className="mt-4 text-3xl font-black text-slate-950 sm:text-4xl">
              Estimate offshore team savings.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Use this directional calculator to compare local hiring cost with a dedicated
              India-based team managed by Variable Soft.
            </p>
            <p className="mt-4 text-sm text-slate-500">
              Final pricing depends on role seniority, technology stack, engagement model,
              project complexity, tools and support needs.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_-55px_rgba(15,23,42,0.5)] sm:p-8">
              <div className="grid gap-6 md:grid-cols-2">
                <label className="space-y-3">
                  <span className="text-sm font-bold text-slate-700">Team size: {teamSize}</span>
                  <input
                    type="range"
                    min={1}
                    max={30}
                    value={teamSize}
                    onChange={(event) => setTeamSize(Number(event.target.value))}
                    className="w-full accent-primary"
                  />
                </label>
                <label className="space-y-2">
                  <span className="text-sm font-bold text-slate-700">Average local cost / person / month</span>
                  <input
                    type="number"
                    min={1000}
                    step={100}
                    value={localCost}
                    onChange={(event) => setLocalCost(Number(event.target.value))}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-primary"
                  />
                </label>
                <label className="space-y-2">
                  <span className="text-sm font-bold text-slate-700">Estimated India cost / person / month</span>
                  <input
                    type="number"
                    min={500}
                    step={100}
                    value={indiaCost}
                    onChange={(event) => setIndiaCost(Number(event.target.value))}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-primary"
                  />
                </label>
                <label className="space-y-2">
                  <span className="text-sm font-bold text-slate-700">Management / PM / QA buffer (%)</span>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    step={1}
                    value={buffer}
                    onChange={(event) => setBuffer(Number(event.target.value))}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-primary"
                  />
                </label>
              </div>

              <div className="mt-8 grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl bg-slate-50 p-5">
                  <div className="text-sm font-semibold text-slate-500">Local monthly</div>
                  <div className="mt-2 text-2xl font-black text-slate-950">{money(estimate.localMonthly)}</div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-5">
                  <div className="text-sm font-semibold text-slate-500">India team monthly</div>
                  <div className="mt-2 text-2xl font-black text-slate-950">{money(estimate.indiaMonthly)}</div>
                </div>
                <div className="rounded-2xl bg-primary p-5 text-white">
                  <div className="text-sm font-semibold text-white/70">Estimated savings</div>
                  <div className="mt-2 text-2xl font-black">{estimate.savingPercent}%</div>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-emerald-100 bg-emerald-50 p-5">
                <div className="text-sm font-bold text-emerald-700">Estimated yearly savings</div>
                <div className="mt-2 text-4xl font-black text-emerald-800">{money(estimate.yearlySaving)}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-18 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-widest text-primary">
                Technology capability
              </span>
              <h2 className="mt-4 text-3xl font-black text-slate-950 sm:text-4xl">
                Built for service delivery, product engineering and automation.
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Your team can work on new product builds, legacy modernization, internal tools,
                dashboards, CRM workflows, data automation and ongoing support.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid gap-3 sm:grid-cols-2">
                {capabilities.map((capability) => (
                  <div key={capability} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold text-slate-700">
                    {capability}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-18 text-white md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-widest text-sky-200">
                Frequently asked questions
              </span>
              <h2 className="mt-4 text-3xl font-black sm:text-4xl">
                Questions before starting a Micro-GCC.
              </h2>
            </div>
            <div className="space-y-4 lg:col-span-7">
              {faqs.map((faq) => (
                <details key={faq.question} className="group rounded-2xl border border-white/10 bg-white/[0.06] p-5">
                  <summary className="cursor-pointer list-none font-bold text-white">
                    {faq.question}
                  </summary>
                  <p className="mt-3 text-white/68">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>

          <div className="mt-14 rounded-[30px] border border-white/10 bg-white/[0.06] p-8 text-center">
            <h2 className="text-3xl font-black">Want to know what your India team would cost?</h2>
            <p className="mx-auto mt-3 max-w-2xl text-white/70">
              Share your required roles, technology stack, expected team size and business goal.
              We will suggest a practical offshore team structure and onboarding plan.
            </p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
              <a
                href="/getintouch"
                className="inline-flex items-center justify-center rounded-2xl bg-white px-7 py-4 font-bold text-slate-950"
              >
                Get Cost Estimate
              </a>
              <a
                href="https://wa.me/918952012870?text=Hi%20Variable%20Soft%2C%20I%20want%20to%20discuss%20a%20Micro-GCC%20or%20dedicated%20offshore%20team."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-7 py-4 font-semibold text-white"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
